import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Chip } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { STATUS_CHIP, STATUS_LABEL_KEY } from '../statusChip';
import type { RowDrawerModel } from './useRowDrawer';

type RowDrawerMetaProps = {
    model: RowDrawerModel;
};

/**
 * The facts in the row drawer's header (`FormDrawer`'s `meta`): the row's
 * status chip, then its SKU, with the group's size and a link icon when
 * the SKU has several rows ("SKU GA55 · 2 rows"). No SKU chip for a row
 * without one.
 */
export const RowDrawerMeta: React.FC<RowDrawerMetaProps> = ({ model }) => {
    const { t } = useTranslation('pages');
    const groupSize = model.groupRows.length;

    return (
        <>
            <Chip
                {...STATUS_CHIP[model.status]}
                data-status={model.status}
                size="sm"
                {...getNodeTestIdProp('catalog-import-row-drawer-status')}
            >
                {t(STATUS_LABEL_KEY[model.status])}
            </Chip>
            {model.sku === '' ? null : (
                <Chip
                    size="sm"
                    variant="soft"
                    {...getNodeTestIdProp('catalog-import-row-drawer-sku')}
                >
                    {groupSize > 1 ? (
                        <Icon
                            aria-hidden
                            className="size-3"
                            icon="gravity-ui:link"
                        />
                    ) : null}
                    <Chip.Label>
                        {groupSize > 1
                            ? t('catalogImport.products.drawer.skuGroup', {
                                  sku: model.sku,
                                  rows: countParam(groupSize),
                              })
                            : t('catalogImport.products.drawer.sku', {
                                  sku: model.sku,
                              })}
                    </Chip.Label>
                </Chip>
            )}
        </>
    );
};
